import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";

import { ApiError, http } from "@/shared/api/http";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";

const CONFIRMATION_PHRASE = "APPLY C.9.2B";

type StripeIdentityCensus = {
  source: string;
  subscription_workspace_owner_class: "founder" | "growth" | "other";
  existing_billing_account_present: boolean;
  subscription_customer_identity_present: boolean;
  founder_projection_customer_identity_present: boolean;
  founder_projection_customer_matches_subscription: boolean;
  existing_billing_account_customer_identity_present: boolean;
  existing_billing_account_customer_matches_subscription: boolean;
  subscription_subscription_identity_present: boolean;
  founder_projection_subscription_identity_present: boolean;
  founder_projection_subscription_matches_subscription: boolean;
  existing_billing_account_subscription_identity_present: boolean;
  existing_billing_account_subscription_matches_subscription: boolean;
  stripe_network_calls: number;
  records_are_not_modified: boolean;
  census_only: boolean;
  apply_authorized: boolean;
  stripe_identity_census_sha256: string;
};

type FounderBillingMigrationStatus = {
  source: string;
  runtime_patch?: string;
  runtime_source_sha256?: string;
  stripe_identity_census?: StripeIdentityCensus;
  certified_target_match: boolean;
  commercial_scope_match: boolean;
  commercial_scope_sha256: string;
  c91_action_plan_sha256: string;
  c91_dry_run_sha256: string;
  preservation_census_sha256: string;
  migration_applied: boolean;
  apply_ready: boolean;
  confirmation_phrase: string;
  billing_account_present: boolean;
  billing_account_core_matches: boolean;
  workspace_links_correct: number;
  subscription_links_correct: number;
  excluded_test_workspace_links_to_founder: number;
  workspace_owner_changes: number;
  authentication_ownership_changes: number;
  stripe_network_calls: number;
  post_apply_state_sha256: string;
  records_are_not_modified: boolean;
  status_only: boolean;
  blocker?: string;
};

type FounderBillingMigrationApplyResult = FounderBillingMigrationStatus & {
  idempotent_replay: boolean;
  billing_account_writes: number;
  workspace_links_written: number;
  subscription_links_written: number;
  records_modified: boolean;
  apply_authorized: boolean;
};

type ApplyBody = {
  confirmation: string;
  commercial_scope_sha256: string;
  corrected_action_plan_sha256: string;
  corrected_dry_run_sha256: string;
  preservation_census_sha256: string;
};

async function loadFounderBillingMigrationStatus(): Promise<FounderBillingMigrationStatus> {
  return http<FounderBillingMigrationStatus>(
    "/billing/admin/founder-billing-migration-status",
    { method: "GET" },
  );
}

async function applyFounderBillingMigration(body: ApplyBody): Promise<FounderBillingMigrationApplyResult> {
  return http<FounderBillingMigrationApplyResult>(
    "/billing/admin/founder-billing-migration-apply",
    { method: "POST", body },
  );
}

export default function FounderBillingMigrationApply(): JSX.Element {
  const [confirmation, setConfirmation] = useState("");
  const statusQuery = useQuery<FounderBillingMigrationStatus, ApiError>({
    queryKey: ["founder", "billing-migration-c92b-status"],
    queryFn: loadFounderBillingMigrationStatus,
    retry: false,
    staleTime: 0,
  });
  const applyMutation = useMutation<FounderBillingMigrationApplyResult, ApiError, ApplyBody>({
    mutationFn: applyFounderBillingMigration,
    onSuccess: async () => {
      setConfirmation("");
      await statusQuery.refetch();
    },
  });

  if (statusQuery.isLoading) {
    return (
      <Card data-testid="c92b-migration-status-loading">
        <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Checking the digest-locked C.9.2B migration status…
        </CardContent>
      </Card>
    );
  }

  if (statusQuery.isError) {
    return (
      <Card className="border-amber-300" data-testid="c92b-migration-status-error">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-700">
            <ShieldAlert className="h-5 w-5" aria-hidden />
            C.9.2B migration control unavailable
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>{statusQuery.error.message}</p>
          <Button variant="outline" onClick={() => void statusQuery.refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
            Retry status
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!statusQuery.data) {
    return (
      <Card data-testid="c92b-migration-status-empty">
        <CardContent className="p-6 text-sm text-muted-foreground">
          C.9.2B migration status returned no data. No write is available.
        </CardContent>
      </Card>
    );
  }

  const status = statusQuery.data;
  if (status.migration_applied) {
    return (
      <Card data-testid="c92b-migration-complete">
        <CardHeader>
          <CardTitle>C.9.2B Founder Billing Account migration complete</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Canonical Billing Account verified · Workspace links {status.workspace_links_correct}/4 · Subscription links {status.subscription_links_correct}/1 · Excluded test workspace links {status.excluded_test_workspace_links_to_founder}
          </p>
          <p className="text-muted-foreground">
            Workspace owner changes {status.workspace_owner_changes} · Authentication ownership changes {status.authentication_ownership_changes} · Stripe network calls {status.stripe_network_calls}
          </p>
          <div className="break-all font-mono text-xs text-muted-foreground">
            post_apply_state_sha256: {status.post_apply_state_sha256}
          </div>
        </CardContent>
      </Card>
    );
  }

  const exactPhrase = status.confirmation_phrase || CONFIRMATION_PHRASE;
  const diagnosticOnly = status.stripe_identity_census?.census_only === true;
  const effectiveApplyReady = status.apply_ready && !diagnosticOnly;
  const ready = effectiveApplyReady && confirmation === exactPhrase;

  return (
    <Card className="border-amber-300" data-testid="c92b-migration-apply-control">
      <CardHeader>
        <CardTitle>C.9.2B — Founder Billing Account apply</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          This is the one-time Production write. It is Founder-only, physical-target guarded, digest-locked, transaction-wrapped, and limited to one canonical Billing Account, four real workspace links, and one subscription link.
        </p>
        <p className="text-muted-foreground">
          Apply ready {effectiveApplyReady ? "yes" : "no"} · Certified target {status.certified_target_match ? "matched" : "blocked"} · Commercial scope {status.commercial_scope_match ? "matched" : "blocked"}
        </p>
        <div
          className="break-all font-mono text-xs text-muted-foreground"
          data-testid="c92b-runtime-attestation"
        >
          Backend runtime {status.runtime_patch || "unattested"} · source_sha256: {status.runtime_source_sha256 || "unattested"}
        </div>
        {status.stripe_identity_census ? (
          <div className="space-y-1 rounded-md border p-3 text-xs text-muted-foreground" data-testid="c92b3-stripe-identity-census">
            <p className="font-medium text-foreground">C.9.2B.3 Stripe identity authority census · diagnostic only</p>
            <p>Subscription owner class {status.stripe_identity_census.subscription_workspace_owner_class} · Existing canonical Billing Account {status.stripe_identity_census.existing_billing_account_present ? "yes" : "no"}</p>
            <p>Customer identity — current subscription {status.stripe_identity_census.subscription_customer_identity_present ? "present" : "absent"} · Founder projection {status.stripe_identity_census.founder_projection_customer_identity_present ? "present" : "absent"} · match {status.stripe_identity_census.founder_projection_customer_matches_subscription ? "yes" : "no"} · Existing Billing Account match {status.stripe_identity_census.existing_billing_account_customer_matches_subscription ? "yes" : "no"}</p>
            <p>Subscription identity — current subscription {status.stripe_identity_census.subscription_subscription_identity_present ? "present" : "absent"} · Founder projection {status.stripe_identity_census.founder_projection_subscription_identity_present ? "present" : "absent"} · match {status.stripe_identity_census.founder_projection_subscription_matches_subscription ? "yes" : "no"} · Existing Billing Account match {status.stripe_identity_census.existing_billing_account_subscription_matches_subscription ? "yes" : "no"}</p>
            <p className="break-all font-mono">stripe_identity_census_sha256: {status.stripe_identity_census.stripe_identity_census_sha256}</p>
            <p>Apply is blocked in the UI while this read-only census is active.</p>
          </div>
        ) : null}
        {status.blocker ? (
          <p className="font-mono text-xs text-amber-700" data-testid="c92b-migration-blocker">
            blocker: {status.blocker}
          </p>
        ) : null}
        <div className="space-y-1 break-all font-mono text-xs text-muted-foreground">
          <p>commercial_scope_sha256: {status.commercial_scope_sha256}</p>
          <p>corrected_action_plan_sha256: {status.c91_action_plan_sha256}</p>
          <p>corrected_dry_run_sha256: {status.c91_dry_run_sha256}</p>
          <p>preservation_census_sha256: {status.preservation_census_sha256}</p>
        </div>
        <div className="space-y-2">
          <label htmlFor="c92b-confirmation" className="block font-medium">
            Type <span className="font-mono">{exactPhrase}</span> exactly to authorize the one-time Production migration.
          </label>
          <input
            id="c92b-confirmation"
            data-testid="c92b-confirmation-input"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
            placeholder={exactPhrase}
          />
        </div>
        {applyMutation.isError ? (
          <p className="text-amber-700" data-testid="c92b-apply-error">
            {applyMutation.error.message}
          </p>
        ) : null}
        <Button
          disabled={!ready || applyMutation.isPending}
          onClick={() => {
            if (!ready) return;
            applyMutation.mutate({
              confirmation,
              commercial_scope_sha256: status.commercial_scope_sha256,
              corrected_action_plan_sha256: status.c91_action_plan_sha256,
              corrected_dry_run_sha256: status.c91_dry_run_sha256,
              preservation_census_sha256: status.preservation_census_sha256,
            });
          }}
          data-testid="c92b-apply-button"
        >
          {applyMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              Applying C.9.2B…
            </>
          ) : (
            "Apply Founder Billing Account migration"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
